// Sortable + paginated Employee Performance table — FE-033 / §7.0.4.
//
// Pure presentation component: state lives in the parent
// ``EmployeePerformanceSection`` (so the data-fetching hook and the
// header controls share a single source of truth). The table itself
// is keyboard-accessible — sort headers are real buttons inside
// ``<th>``, pagination buttons announce their action via
// aria-labels.

import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

import {
  buildCommitmentFilterUrl,
  type DashboardDateRange,
  type EmployeePerformanceRow,
} from "@/features/dashboard";
import { Table, THead, TBody, Tr, Th, Td } from "@/design-system";

import { formatStaffName } from "./displayName";


export type EmployeePerformanceSortKey =
  | "completed"
  | "points"
  | "completion_rate"
  | "on_time_percent"
  | "past_due"
  | "utilization";


export interface EmployeePerformanceTableProps {
  rows: EmployeePerformanceRow[];
  total: number;
  sortBy: EmployeePerformanceSortKey;
  sortDir: "asc" | "desc";
  onSortChange: (next: {
    sortBy: EmployeePerformanceSortKey;
    sortDir: "asc" | "desc";
  }) => void;
  limit: number;
  offset: number;
  onOffsetChange: (next: number) => void;
  dateRange: DashboardDateRange;
}


interface ColumnConfig {
  key: EmployeePerformanceSortKey | "display_name";
  label: string;
  sortKey?: EmployeePerformanceSortKey;
  align?: "left" | "right";
  format?: (row: EmployeePerformanceRow) => string;
  testKey?: string;
}


const COLUMNS: ColumnConfig[] = [
  {
    key: "display_name",
    label: "Staff",
    align: "left",
    format: (row) => formatStaffName(row),
    testKey: "staff",
  },
  {
    key: "completed",
    label: "Completed",
    sortKey: "completed",
    align: "right",
    format: (row) => String(row.completed),
    testKey: "completed",
  },
  {
    key: "points",
    label: "Points",
    sortKey: "points",
    align: "right",
    format: (row) => String(row.points),
    testKey: "points",
  },
  {
    key: "utilization",
    label: "Utilization",
    sortKey: "utilization",
    align: "right",
    format: (row) => `${Math.round(row.utilization * 100)}%`,
    testKey: "utilization",
  },
  {
    key: "completion_rate",
    label: "Completion",
    sortKey: "completion_rate",
    align: "right",
    format: (row) => `${Math.round(row.completion_rate * 100)}%`,
    testKey: "completion-rate",
  },
  {
    key: "on_time_percent",
    label: "On-time",
    sortKey: "on_time_percent",
    align: "right",
    format: (row) => `${Math.round(row.on_time_percent)}%`,
    testKey: "on-time",
  },
  {
    key: "past_due",
    label: "Past due",
    sortKey: "past_due",
    align: "right",
    format: (row) => String(row.past_due),
    testKey: "past-due",
  },
];


export function EmployeePerformanceTable({
  rows, total, sortBy, sortDir, onSortChange,
  limit, offset, onOffsetChange, dateRange,
}: EmployeePerformanceTableProps) {
  const showingTo = Math.min(offset + rows.length, total);
  const showingFrom = rows.length === 0 ? 0 : offset + 1;
  const hasPrev = offset > 0;
  const hasNext = offset + rows.length < total;
  return (
    <div data-testid="employee-performance-table" className="flex flex-col">
      <Table wrapperClassName="rounded-md border border-border-subtle">
        <THead className="bg-recessed text-xs font-medium uppercase tracking-wide text-text-tertiary">
          <Tr>
            {COLUMNS.map((col) => (
              <Th
                key={col.key}
                className={col.align === "right" ? "text-right" : ""}
                aria-sort={
                  col.sortKey === sortBy
                    ? sortDir === "asc" ? "ascending" : "descending"
                    : col.sortKey ? "none" : undefined
                }
              >
                {col.sortKey ? (
                  <SortHeaderButton
                    label={col.label}
                    active={col.sortKey === sortBy}
                    sortDir={col.sortKey === sortBy ? sortDir : null}
                    onClick={() =>
                      onSortChange(
                        col.sortKey === sortBy
                          ? {
                              sortBy: col.sortKey,
                              sortDir: sortDir === "asc" ? "desc" : "asc",
                            }
                          : { sortBy: col.sortKey!, sortDir: "desc" },
                      )
                    }
                  />
                ) : (
                  col.label
                )}
              </Th>
            ))}
            <Th className="w-8 text-right">
              <span className="sr-only">Drill through</span>
            </Th>
          </Tr>
        </THead>
        <TBody>
          {rows.map((row) => (
            <Tr
              key={row.user_id}
              data-testid={`employee-performance-row-${row.user_id}`}
              className="border-t border-border-subtle"
            >
              {COLUMNS.map((col) => {
                const value = col.format ? col.format(row) : "";
                return (
                  <Td
                    key={col.key}
                    data-testid={
                      col.testKey
                        ? `cell-${col.testKey}-${row.user_id}`
                        : undefined
                    }
                    className={`text-text-primary ${col.align === "right" ? "text-right" : ""}`}
                  >
                    {value}
                  </Td>
                );
              })}
              <Td className="text-right">
                <Link
                  to={buildCommitmentFilterUrl(
                    { assigned_to: row.user_id },
                    dateRange,
                  )}
                  data-testid={`drill-${row.user_id}`}
                  aria-label={
                    `View ${formatStaffName(row)}'s Commitments in the Commitment Tracker`
                  }
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full text-text-tertiary transition-fast ease-out hover:bg-recessed hover:text-text-primary"
                >
                  <ChevronRight size={14} aria-hidden="true" />
                </Link>
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>

      <div className="mt-2 flex items-center justify-between text-xs text-text-tertiary">
        <span data-testid="employee-performance-pagination-summary">
          {total === 0
            ? "0 staff"
            : `Showing ${showingFrom}–${showingTo} of ${total}`}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            data-testid="employee-performance-prev"
            disabled={!hasPrev}
            onClick={() => onOffsetChange(Math.max(0, offset - limit))}
            aria-label="Previous page"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-secondary disabled:cursor-not-allowed disabled:text-text-tertiary hover:bg-recessed"
          >
            <ChevronLeft size={14} aria-hidden="true" />
          </button>
          <button
            type="button"
            data-testid="employee-performance-next"
            disabled={!hasNext}
            onClick={() => onOffsetChange(offset + limit)}
            aria-label="Next page"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-text-secondary disabled:cursor-not-allowed disabled:text-text-tertiary hover:bg-recessed"
          >
            <ChevronRight size={14} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}


function SortHeaderButton({
  label, active, sortDir, onClick,
}: {
  label: string;
  active: boolean;
  sortDir: "asc" | "desc" | null;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={
        active
          ? `Sort by ${label}, currently ${sortDir === "asc" ? "ascending" : "descending"}`
          : `Sort by ${label}`
      }
      className={[
        "inline-flex items-center gap-1 transition-fast ease-out",
        active ? "text-text-primary" : "hover:text-text-primary",
      ].join(" ")}
    >
      <span>{label}</span>
      {active && sortDir === "asc" ? (
        <ArrowUp size={10} aria-hidden="true" />
      ) : active && sortDir === "desc" ? (
        <ArrowDown size={10} aria-hidden="true" />
      ) : null}
    </button>
  );
}
